const memoryRequests = new Map()
const memoryFallbackKeys = new Set()

export const getBrowserStorage = (storageName = 'localStorage') => {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return window[storageName]
  } catch {
    return null
  }
}

export const createStableRequestId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID()
  }

  return `request-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const normalizeForStorage = (value) => {
  if (Array.isArray(value)) {
    return value.map(normalizeForStorage)
  }

  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((normalized, key) => {
        normalized[key] = normalizeForStorage(value[key])
        return normalized
      }, {})
  }

  return value
}

export const buildStableRequestStorageKey = (prefix, descriptor) =>
  `${prefix}:${encodeURIComponent(JSON.stringify(normalizeForStorage(descriptor)))}`

const readRequestId = (storageKey, storage) => {
  if (!storage) {
    memoryFallbackKeys.add(storageKey)
    return memoryRequests.get(storageKey) || null
  }

  try {
    const storedRequestId = storage.getItem(storageKey)
    if (storedRequestId) {
      memoryRequests.set(storageKey, storedRequestId)
      memoryFallbackKeys.delete(storageKey)
      return storedRequestId
    }

    return memoryFallbackKeys.has(storageKey)
      ? memoryRequests.get(storageKey) || null
      : null
  } catch {
    memoryFallbackKeys.add(storageKey)
    return memoryRequests.get(storageKey) || null
  }
}

const writeRequestId = (storageKey, requestId, storage) => {
  memoryRequests.set(storageKey, requestId)

  if (!storage) {
    memoryFallbackKeys.add(storageKey)
    return
  }

  try {
    storage.setItem(storageKey, requestId)
    memoryFallbackKeys.delete(storageKey)
  } catch {
    memoryFallbackKeys.add(storageKey)
  }
}

export const getOrCreateRequestByStorageKey = (
  storageKey,
  storage = getBrowserStorage(),
  createId = createStableRequestId
) => {
  const storedRequestId = readRequestId(storageKey, storage)

  if (storedRequestId) {
    return {requestId: storedRequestId, storageKey}
  }

  const requestId = createId()
  writeRequestId(storageKey, requestId, storage)
  return {requestId, storageKey}
}

export const getOrCreateStableRequest = (
  prefix,
  descriptor,
  storage = getBrowserStorage(),
  createId = createStableRequestId
) => getOrCreateRequestByStorageKey(
  buildStableRequestStorageKey(prefix, descriptor),
  storage,
  createId
)

export const clearStableRequest = (
  storageKey,
  storage = getBrowserStorage()
) => {
  memoryRequests.delete(storageKey)
  memoryFallbackKeys.delete(storageKey)
  try {
    storage?.removeItem(storageKey)
  } catch {
    // There is no persistent copy to clear when storage is blocked.
  }
}
